import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import AutoTranslateButton from '../tools/AutoTranslateButton';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';

export default function TissHeading({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
  const { t } = useTranslation();
  const cookie = getCookies();

  // Helper to get multilingual value
  const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
    if (!field) return fallback;
    if (typeof field === 'string') return field; // Legacy support
    if (typeof field === 'object') {
      return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
    }
    return fallback;
  };

  // Helper to set multilingual value
  const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
    if (!currentData || typeof currentData !== 'object') {
      return { [lang]: value };
    }
    return {
      ...currentData,
      [lang]: value
    };
  };

  // Get current text value for the active language
  const getCurrentTextForLang = () => {
    const textField = data?.data?.text || {};
    return getMultilingualValue(textField, activeLang);
  };

  const initialMode = data?.data?.mode || 'h1';

  // Initialize multilingual data from props
  const initializeMultilingualData = () => {
    const textField = data?.data?.text || {};
    if (typeof textField === 'object' && textField !== null && !Array.isArray(textField)) {
      return { ...textField };
    } else if (typeof textField === 'string' && textField) {
      return { en: textField };
    }
    return {};
  };

  // State - Store ALL multilingual data in state (all 5 languages at once)
  const [multilingualText, setMultilingualText] = React.useState<any>(initializeMultilingualData());
  const [mode, setMode] = React.useState(initialMode);

  // Get current language text from multilingual state
  const currentText = multilingualText[activeLang] || '';
  const hasContent = !!currentText || Object.values(multilingualText).some((val: any) => !!val);

  const [isEditing, setIsEditing] = React.useState(!hasContent);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const prevLangRef = React.useRef(activeLang);
  const prevDataIdRef = React.useRef(data?._id);

  // Update multilingual state when data from server changes
  React.useEffect(() => {
    if (prevDataIdRef.current !== data?._id) {
      const textField = data?.data?.text || {};

      // Initialize multilingual data from server
      let newMultilingualData: any;
      if (typeof textField === 'object' && textField !== null && !Array.isArray(textField)) {
        // Merge with existing state to preserve languages
        newMultilingualData = { ...multilingualText, ...textField };
      } else if (typeof textField === 'string' && textField) {
        // Legacy format - preserve existing languages, add this as 'en'
        newMultilingualData = { ...multilingualText, en: textField };
      } else {
        // Empty - keep existing multilingual data
        newMultilingualData = { ...multilingualText };
      }

      setMultilingualText(newMultilingualData);
      setHasChanges(false);
      prevDataIdRef.current = data?._id;
    }

    // Update ref when language changes
    if (prevLangRef.current !== activeLang) {
      setHasChanges(false);
      prevLangRef.current = activeLang;
    }
  }, [data?._id, data?.data?.text, activeLang]);

  // Track changes - compare with multilingual state
  React.useEffect(() => {
    const originalText = data?.data?.text || {};
    const originalForLang = typeof originalText === 'object' && originalText !== null
      ? (originalText[activeLang] || '')
      : (activeLang === 'en' ? originalText : '');
    setHasChanges(currentText !== originalForLang || mode !== initialMode);
  }, [currentText, mode, activeLang, initialMode, data?.data?.text]);

  // Save handler - save ALL languages at once
  const saveHeading = async () => {
    if (!data?._id) return;

    setSaving(true);
    try {
      console.log('=== SAVING ALL MULTILINGUAL TEXT ===');
      console.log('All languages in state:', multilingualText);

      // Send ALL multilingual data to backend
      const dataToSend = {
        text: multilingualText, // Send complete multilingual object
        mode // Preserve mode
      };

      console.log('Sending to backend:', JSON.stringify(dataToSend, null, 2));

      await axios.put(
        `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
        dataToSend,
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );

      Swal.fire({
        title: t('Saved successfully'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
      });
      setIsEditing(false);
      setHasChanges(false);
      reload();
    } catch (error) {
      console.error('Save failed:', error);
      Swal.fire({
        title: t('Error saving'),
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel handler with confirmation if there are changes
  const handleCancel = () => {
    if (hasChanges) {
      Swal.fire({
        title: t('Discard changes?'),
        text: t('You have unsaved changes. Are you sure you want to cancel?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('Yes, discard'),
        cancelButtonText: t('No, keep editing'),
      }).then((result) => {
        if (result.isConfirmed) {
          // Reload from server to reset all changes
          reload();
          setIsEditing(false);
          setHasChanges(false);
        }
      });
    } else {
      setIsEditing(false);
    }
  };

  // Render heading dynamically
  const renderHeading = () => {
    const Tag = mode as keyof JSX.IntrinsicElements;
    const fontSizeMap: Record<string, string> = {
      h1: 'text-3xl',
      h2: 'text-2xl',
      h3: 'text-xl',
      h4: 'text-lg',
      h5: 'text-base',
      h6: 'text-sm',
    };
    const className = `${fontSizeMap[mode] || 'text-base'} font-bold text-right`;

    return React.createElement(Tag, { className }, currentText || t('بدون عنوان'));
  };

  // Placeholder view (when no content)
  if (!hasContent && !isEditing) {
    return (
      <div
        className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
        onClick={() => setIsEditing(true)}
        dir="rtl"
      >
        <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
          <img src="/icons/addheading.svg" className="h-8 w-8" alt={t('افزودن عنوان')} />
        </div>
        <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
          {t('کلیک برای افزودن عنوان')}
        </span>
        <span className="text-gray-400 text-xs mt-1">
          {t('Start typing to create heading')}
        </span>
      </div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
        <div className="p-0.5">
          <div className="space-y-4">
            <div>
              <label htmlFor="heading-mode" className="block text-sm font-medium text-gray-700 mb-2">
                {t('نوع عنوان')}
              </label>
              <select
                id="heading-mode"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                className="w-full py-2.5 px-4 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white hover:border-gray-400"
              >
                <option value="h1">{t('عنوان 1')} (H1)</option>
                <option value="h2">{t('عنوان 2')} (H2)</option>
                <option value="h3">{t('عنوان 3')} (H3)</option>
                <option value="h4">{t('عنوان 4')} (H4)</option>
                <option value="h5">{t('عنوان 5')} (H5)</option>
                <option value="h6">{t('عنوان 6')} (H6)</option>
              </select>
            </div>

            <div>
              <label htmlFor="heading-text" className="block text-sm font-medium text-gray-700 mb-2">
                {t('Heading Text')}
              </label>
              <input
                key={`heading-input-${activeLang}`}
                id="heading-text"
                type="text"
                value={currentText}
                onChange={(e) => {
                  const newText = e.target.value;
                  // Update only the current language, preserve all others
                  setMultilingualText((prev: any) => ({
                    ...prev,
                    [activeLang]: newText
                  }));
                }}
                placeholder={t('Enter your heading...')}
                className="w-full py-2.5 px-4 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
                autoFocus
              />
            </div>

            {/* Preview */}
            {currentText && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 mb-2">{t('Preview')}:</p>
                {renderHeading()}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-5 flex gap-3 justify-between items-center">
            <div className="flex items-center gap-2">
              <AutoTranslateButton
                sourceText={multilingualText.en || ''}
                targetLang={activeLang}
                onTranslated={(translatedText) => {
                  setMultilingualText((prev: any) => ({
                    ...prev,
                    [activeLang]: translatedText
                  }));
                  setHasChanges(true);
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                  {t('Unsaved changes')}
                </span>
              )}
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                {t('Cancel')}
              </button>
              <button
                type="button"
                onClick={saveHeading}
                disabled={saving || !currentText.trim()}
                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t('Saving...')}</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{t('Save')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Preview Mode
  return (
    <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
      <div className="p-0.5 relative">
        {renderHeading()}

        {/* Edit Button - Appears on hover */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <button
            onClick={() => setIsEditing(true)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('Edit Heading')}
          </button>
        </div>
      </div>
    </div>
  );
}